
import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ChevronUp, ChevronDown, Droplet } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { NutritionJournal } from '@/types';
import { triggerLisaFeedback } from '@/utils/onboardingEvents';
import { useMenu } from '@/contexts/MenuContext';
import { format } from 'date-fns';
import { getMenuDayFromDate } from '@/utils/menuJournalSync';

interface LisaAdviceProps {
  journal: NutritionJournal;
  user: any; // Using any to avoid type issues with the User type
}

const LisaAdvice: React.FC<LisaAdviceProps> = ({ journal, user }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const { weeklyMenu, getMenuTotals } = useMenu();
  
  const getNutritionAdvice = () => {
    const today = new Date();
    const menuDay = getMenuDayFromDate(today);
    const menuTotals = weeklyMenu ? getMenuTotals(menuDay) : null;
    
    // Check if nutrition data exists in journal OR in menu
    const hasJournalMeals = journal?.nutritionSummary && journal.nutritionSummary.caloriesConsumed > 0;
    const hasMenuMeals = menuTotals && menuTotals.calories > 0;
    
    if (!hasJournalMeals && !hasMenuMeals) {
      return "Aún no has registrado comidas hoy. Recuerda que una alimentación balanceada es clave para tu bienestar.";
    }
    
    // Use either journal or menu data, prioritizing journal if available
    const caloriesConsumed = hasJournalMeals ? journal.nutritionSummary.caloriesConsumed : menuTotals?.calories || 0;
    const goalCalories = journal?.nutritionSummary?.goalCalories > 0 
      ? journal.nutritionSummary.goalCalories 
      : (weeklyMenu?.nutritionGoals?.calories || 2000);
      
    const caloriesPercentage = (caloriesConsumed / goalCalories) * 100;
      
    if (caloriesPercentage < 30) {
      return "Tu consumo calórico es bajo para esta hora del día. ¿Qué tal si agregas una comida nutritiva?";
    } 
    
    if (caloriesPercentage < 70) {
      return "Vas bien con tu alimentación. Asegúrate de incluir proteínas y vegetales en tu próxima comida.";
    }
    
    if (caloriesPercentage < 90) {
      return "¡Casi has alcanzado tus objetivos! Una pequeña merienda saludable podría completar tus necesidades.";
    }
    
    if (caloriesPercentage <= 110) {
      return "¡Perfecto! Has alcanzado un balance ideal en tu consumo calórico del día.";
    }
    
    return "Has superado el objetivo calórico. Considera opciones más ligeras para el resto del día.";
  };
  
  const getHydrationAdvice = () => {
    // Safety check for journal and waterIntake
    if (!journal || typeof journal.waterIntake !== 'number') {
      return "No olvides mantenerte hidratado durante el día. ¡El agua es esencial!";
    }
    
    // Updated to use 6 glasses as the target
    const waterPercentage = (journal.waterIntake / 6) * 100;
    
    if (waterPercentage < 30) {
      return "Recuerda mantenerte hidratado. El agua es esencial para tu bienestar.";
    }
    
    if (waterPercentage < 60) {
      return "Vas bien con tu hidratación, pero aún necesitas más agua. ¿Qué tal si bebes un vaso ahora?";
    }
    
    if (waterPercentage < 90) {
      return "¡Casi llegas a tu meta de hidratación! Unos vasos más y estarás perfecto.";
    }
    
    return "¡Excelente hidratación! Mantener este hábito mejorará tu energía y salud.";
  };
  
  const handleAvatarClick = () => {
    if (user?.id) {
      triggerLisaFeedback(user.id, 'success');
    }
  };

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 shadow-md overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-2 bg-purple-100/50">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Avatar 
                className="h-14 w-14 ring-2 ring-purple-300 ring-offset-2 transition-transform hover:scale-105 cursor-pointer"
                onClick={handleAvatarClick}
              >
                <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                <AvatarFallback className="bg-purple-200 text-purple-700">L</AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-xl text-purple-800">Lisa</CardTitle>
                <p className="text-sm text-purple-600 opacity-80">Tu asistente nutricional</p>
              </div>
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="hover:bg-purple-100">
                {isOpen ? <ChevronUp className="h-5 w-5 text-purple-500" /> : <ChevronDown className="h-5 w-5 text-purple-500" />}
              </Button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-4 bg-white/50">
            <div className="space-y-3">
              <div className="bg-purple-50 rounded-lg p-4 shadow-sm">
                <p className="text-sm text-purple-900 leading-relaxed">{getNutritionAdvice()}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 shadow-sm">
                <p className="text-sm text-purple-900 leading-relaxed">{getHydrationAdvice()}</p>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default LisaAdvice;
